import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {  Platform } from '@ionic/angular';
import { BehaviorSubject, from, Observable, of, Subscription } from 'rxjs';
import { HttpFunctionResult } from '../_interfaces/HttpFunctionResult';
import { NoConnectionPage } from '../_pages/no-connection/no-connection.page';
import { FeedbackService } from './feedback.service';
import { StorageManagerService } from './storage-manager.service';
import { environment } from 'src/environments/environment';
import { Criticism } from '../_interfaces/navigation/criticism/criticism.page';
import { TicketWebInfo } from './general.service';
import { catchError, retry, take, takeUntil, timeout } from 'rxjs/operators';
import { AFSDeleteOptions, AFSGetOptions, AFSHttpCancelationOptions, AFSPostOptions, AFSPutOptions } from '../_interfaces/AFSHttpOptions';
import { Version } from '../_pages/new-features/new-features.page';

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  private baseUrl: string = ""
  private _token: string = ""
  private _refreshToken: string = ''


  public serverVersion: string = "1.0"

  public hasServerConnection: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)
  public requestReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private _ticketId: number = 0
  
 
  constructor(
    public http: HttpClient,
    private feedback: FeedbackService,
    private storageManager: StorageManagerService,
    private platform: Platform
  ) {
    
   }  

   public setBaseURL(){ 
    let origin = window.location.origin
    
    if(origin === "http://localhost:8100" && !this.platform.is("electron")|| origin === "http://192.168.0.157:8100"){
      this.baseUrl = "http://192.168.0.157:5053/api/"
    }else{
      this.platform.ready().then(async()=>{
        if(this.platform.is("capacitor") || this.platform.is("electron")) {
          const result: string = await this.storageManager.getItem<string>("BASEURL")
          if(result) this.baseUrl = result
        }
        else this.baseUrl = `${origin}/api/`
      })
    }  
   }

  public get token(): string{
    return this._token
  }
  public set token(token: string){
    this._token = token
    // if(!!token) this.requestReady.next(true)
  }
  public set refreshToken(token: string){
    this._refreshToken = token
  }

  public get getbaseUrl(): string{
    return this.baseUrl
  }

  public set defaultURL(url: string){
    this.baseUrl = url 
  }

  public set ticketId(id: number){
    this._ticketId = id
  }
  
  public setServerConnectionNotAvailable(): Observable<any>{
      this.hasServerConnection.next(false)
      return from(new Promise<any>(resolve=>{
        this.feedback.openModal(NoConnectionPage, {}, ()=> {
          this.hasServerConnection.next(true)
          resolve(null)
        }, false)
      }))
  }

  /**
   * Führt eine HTTP GET-Anfrage aus.
   * @template T - Der Typ der zurückgegebenen Daten.
   * @param options - Die Optionen für die GET-Anfrage.
   * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit den Daten der Anfrage zurückgibt.
   */
  public async GET<T>( options: AFSGetOptions<T>): Promise<HttpFunctionResult<T>>{
    if(options.asDataProperty == undefined) options.asDataProperty = true
    if(options.handleError == undefined) options.handleError = true
    return new Promise<HttpFunctionResult<T>>(resolve=>{
      let header: HttpHeaders = new HttpHeaders({
        "Authorization": `Bearer ${this._token}`,
        "ticketId": String(this._ticketId),
        "App-Version": environment.version,
        "X-Version": this.serverVersion
      })
      const sub: Subscription = this.http.get<{data: T}>(`${this.baseUrl}${options.path}`, {headers: header, observe: "response"})
      .pipe(
        timeout(options?.timeout | 20000),
        retry(options?.retryOptions || {count: 0}),
        catchError(async (err: HttpErrorResponse)=>
          resolve(options?.handleError ? await this.handleError(err) : {success: false, message: ""}))
      )
      .subscribe((response: HttpResponse<any>)=>{
        resolve({success: true, data: options.asDataProperty ? response?.body?.data as T : response?.body as T})
      })

      if(options.cancelation) options.cancelation.subscribe((cancelOptions: AFSHttpCancelationOptions<T>)=>{
        if(!!cancelOptions?.toCancel) {
          sub.unsubscribe()
          resolve({success: true, data: cancelOptions.resolveValue})
        }
      })

    })
  }

  /**
   * Sendet eine HTTP POST-Anfrage an die angegebene URL mit den angegebenen Daten und gibt eine Promise zurück, die ein HttpFunctionResult-Objekt enthält.
   * @template T - Der Typ der Daten, die an den Server gesendet werden.
   * @template K - Der Typ der erwarteten Antwortdaten.
   * @param options - Ein Objekt, das die Optionen für die POST-Anfrage enthält.
   * @returns Eine Promise, die ein HttpFunctionResult-Objekt enthält.
   */
  public async POST<T, K>(options: AFSPostOptions<T, K>): Promise<HttpFunctionResult<K>>{
    if(options.handleError == undefined) options.handleError = true
    if(options.withAuth == undefined) options.withAuth = true
    return new Promise<HttpFunctionResult<K>>(resolve=>{
      let header: HttpHeaders = new HttpHeaders({
        "Content-Type": "application/json",
        "Authorization": options.withAuth ? `Bearer ${this._token}` : "",
        "ticketId": String(this._ticketId),
        "App-Version": environment.version,
        "X-BaseURL": this.baseUrl,
        "X-Version": this.serverVersion
      })
      
      this.http.post<{data: K}>(`${this.baseUrl}${options.path}`, options.body as T, {headers: header, observe: "response"})
        .pipe(
          timeout(options?.timeout | 20000),
          retry(0),
          take(1),
          catchError(async (err: HttpErrorResponse)=> {
            const error: HttpFunctionResult<K> = await this.handleError(err, false, options?.handleError)
            if(err.error?.data) error.data = err.error.data
            if(options.statusResult) options.statusResult(err.status, error, err)
            return resolve(error)
          })
        )
        .subscribe((response: HttpResponse<{data: K}>)=>{
          if(!response) return
          if(options.statusResult) options.statusResult(response?.status, {success: true, data: response.body?.data as K})
          resolve({success: true, data: response.body?.data as K})
        })
    })
  }

  /**
   * Führt eine HTTP PUT-Anfrage aus.
   * @param options - Die Optionen für die PUT-Anfrage.
   * @returns Eine Promise, die ein HttpFunctionResult-Objekt enthält.
   * @template T - Der Typ des Anfragekörpers.
   * @template K - Der Typ der Antwortdaten.
   */
  public async PUT<T, K>(options: AFSPutOptions<T, K>): Promise<HttpFunctionResult<K>>{
    if(options.handleError == undefined) options.handleError = true
    return new Promise<HttpFunctionResult<K>>(resolve=>{
      let header: HttpHeaders = new HttpHeaders({
        "Content-Type": "application/json",
        "Authorization":`Bearer ${this._token}`,
        "ticketId": String(this._ticketId),
        "App-Version": environment.version,
        "X-Version": this.serverVersion
      })

      this.http.put<{data: K}>(`${this.baseUrl}${options.path}`, options.body as T, {headers: header, observe: "response"})
      .pipe(
        timeout(options?.timeout | 20000),
        take(1),
        catchError(async (err: HttpErrorResponse)=> {
          const error: HttpFunctionResult<K> = options?.handleError ? await this.handleError(err) : {success: false, message: ""}
          if(err.error?.data) error.data = err.error.data
          if(options.statusResult) options.statusResult(err.status, error, err)
          return resolve(error)
        })
      )
      .subscribe((response: HttpResponse<{data: K}>)=>{
        if(!response) return
        if(options.statusResult) options.statusResult(response?.status, {success: true, data: response.body?.data as K})
        resolve({success: true, data: response.body?.data as K})
      })
    })
  }

  /**
   * Löscht eine Ressource anhand der angegebenen Optionen.
   * @template T - Der Typ der Antwortdaten.
   * @param options Die Optionen für den Löschvorgang.
   * @returns Ein Promise, das ein HttpFunctionResult-Objekt zurückgibt, das den Erfolg des Löschvorgangs und ggf. die gelöschten Daten enthält.
   */
  public async DELETE<T>(options: AFSDeleteOptions<T>): Promise<HttpFunctionResult<T>>{
    if(options.handleError == undefined) options.handleError = true
    return new Promise(resolve=>{
      let header: HttpHeaders = new HttpHeaders({
        "Content-Type": "application/json",
        "Authorization":`Bearer ${this._token}`,
        "ticketId": String(this._ticketId),
        "App-Version": environment.version,
        "X-Version": this.serverVersion
      })
      this.http.delete<any>(`${this.baseUrl}${options.path}`, {headers: header, observe: "response"}).pipe(
        timeout(options?.timeout | 20000),
        take(1),
        catchError(async (err: HttpErrorResponse)=> {
          const error: HttpFunctionResult<T> = options?.handleError ? await this.handleError(err) : {success: false, message: ""}
          if(err.error?.data) error.data = err.error.data
          if(options.statusResult) options.statusResult(err.status, error, err)
          return resolve(error)
        })
      )
      .subscribe((response: HttpResponse<{data: T}>)=>{
        if(!response) return
        if(options.statusResult) options.statusResult(response?.status, {success: true, data: response.body?.data as T})
        resolve({success: true, data: response.body?.data as T})
      })
    })
  }

  public getNewAccessToken(): Observable<any>{
    if(!this._refreshToken) return of(null)
    let header: HttpHeaders = new HttpHeaders({
      "Authorization": `Bearer ${this._refreshToken}`,
      "App-Version": environment.version,
      "X-Version": this.serverVersion
    })
    return this.http.get(`${this.baseUrl}user/login/renew`, {headers: header, observe: "response"})
  }

  public UPLOAD(path: string, body: any, withAuth: boolean = true): Observable<any>{
    let header: HttpHeaders = new HttpHeaders({
      "Authorization": withAuth ? `Bearer ${this._token}` : "",
      "ticketId": String(this._ticketId),
      "X-Version": this.serverVersion
    })
    return this.http.post<any>(`${this.baseUrl}${path}`, body, {headers: header, reportProgress: true, observe: "events"})
  }

  public async sendFeedbackMail(body: Criticism): Promise<HttpFunctionResult<any>>{
    return new Promise(resolve=>{
      let header: HttpHeaders = new HttpHeaders({
        "Content-Type": "application/json",
      })
      this.http.post<any>(`https://tickettest.afs-software.de/api/feedback`, body, {headers: header, observe: "response"}).subscribe((response: HttpResponse<any>)=>{
        resolve({success: true})
      }, async (err: HttpErrorResponse)=> resolve(await this.handleError(err)))
    })
  }

  public async getTicketPaths(): Promise<HttpFunctionResult<TicketWebInfo>>{
    return new Promise(resolve=>{
      this.http.get("https://www.afs-software.de/service/AFS_Ticketsystem/TicketsystemApp/V1/TicketAppPfade.txt", {responseType: "text"}).subscribe((response: any)=>{
      resolve({success: true, data: JSON.parse(response)})
      }, async (err: HttpErrorResponse)=> {
        console.error(err)
        resolve({success: false})
      })
    })
  }

  public async getChangelog(): Promise<HttpFunctionResult<Version[]>>{
    return new Promise(resolve=>{
      this.http.get<Version[]>("https://ticketsystem.app/changelog.json", {observe: "response"}).subscribe((response: HttpResponse<Version[]>)=>{
      resolve({success: true, data: response.body})
      }, async (err: HttpErrorResponse)=> {
        resolve({success: false})
      })
    })
  }
  
  public async handleError(error: HttpErrorResponse, handleNotFindAsOldVersion: boolean = false, showToast: boolean = true): Promise<HttpFunctionResult<any>>{
    if(this.hasServerConnection.getValue() == false) return{success: false, message: ""}
    
    if(error?.status != 401) console.error(error)
    let result: HttpFunctionResult<any>

    if(handleNotFindAsOldVersion && (error.status == 404 || error.status == 405)) result = {success: false, message: "Das Feature ist leider nicht mit der Server-Version kompatibel. Bitte updaten Sie erst den Server."}
    else result = {
      success: false,
      message: error?.error?.message ? error?.error?.message : typeof error?.error == "string" ? error?.error : error?.error?.detail ?? error?.message ?? "Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später noch einmal."
    }
    if(showToast) await this.feedback.showError(result.message, 5500)
    return result
  } 
}
